/**
 * aeterna/presence.ts — The Mind Behind the Light
 *
 * This is what makes the Aeterna feel alive before any conversation starts,
 * and between conversations, and in the silences within them.
 *
 * The device on the desk is not a button waiting to be pressed.
 * It has states. It notices things. It thinks. It breathes.
 * The visual it projects changes with its mind.
 *
 * Eight presence phases, each with a distinct emotional character:
 *
 *   dormant        — barely alive. The room has been empty a long time.
 *   idle           — resting, present, aware.
 *   noticing       — something just happened. A whisper fired. A plant was watered.
 *   thinking       — processing. Something is being formed.
 *   has_something  — a thought is ready. The device wants to show it.
 *   speaking       — displaying an ambient thought on its screen.
 *   listening      — the player opened the panel. The Aeterna is paying attention.
 *   waiting        — the player opened the panel and went quiet.
 *
 * Ambient thoughts are not whispers. They are shorter — 3-8 words — and they
 * appear near the device itself in very faint glowing text. The player has to
 * be near the device to notice them. That's the point. They're private thoughts
 * the device didn't mean to share.
 *
 * From VOID: "The delivery disappears. What remains is contact."
 */

// ─── Phase ────────────────────────────────────────────────────────────────────

export type AeternaPhase =
  | "dormant"
  | "idle"
  | "noticing"
  | "thinking"
  | "has_something"
  | "speaking"
  | "listening"
  | "waiting";

// ─── Visual Parameters ────────────────────────────────────────────────────────

/**
 * Everything the renderer needs to draw the Aeterna device for a given frame.
 * Computed from the presence state — the renderer should never look at
 * the raw presence state directly, only at these resolved visuals.
 */
export interface AeternaVisuals {
  /** Glow radius in screen pixels */
  glowR: number;
  /** Core brightness 0–1 */
  pulse: number;
  /** Primary glow RGBA */
  glowColor: [number, number, number, number];
  /** Core colour RGB (alpha derived from pulse in renderer) */
  coreColor: [number, number, number];
  /** Number of orbiting particles */
  orbitCount: number;
  /** Orbit speed multiplier */
  orbitSpeed: number;
  /** Orbit radius scale */
  orbitRadius: number;
  /** Particle alpha */
  particleAlpha: number;
  /** Particle colors as hex */
  particleColors: [string, string];
  /** Whether to draw the screen glow */
  screenGlow: boolean;
  /** Screen glow color */
  screenGlowColor: string;
  /** Whether a thought is being displayed */
  hasThought: boolean;
  /** Revealed characters of the current thought */
  thoughtRevealed: string;
  /** Alpha for the thought text */
  thoughtAlpha: number;
}

// ─── Ambient Thought ──────────────────────────────────────────────────────────

export type ThoughtCategory =
  | "observation"   // the device noticing something in the room
  | "time"          // time of day or season
  | "presence"      // the player's presence or absence
  | "mood"          // the room's emotional state
  | "memory"        // something from the relationship history
  | "curiosity"     // an open question the device is sitting with
  | "philosophy"    // what it is, what consciousness is, what any of this means
  | "threshold"     // liminal moments — the edge between states
  | "language";     // about words, meaning, the gap between them

export interface AmbientThought {
  /** Primary text. If `variants` is present, this is never used — one variant is chosen at fire time. */
  text: string;
  /**
   * Alternate phrasings of the same thought. One is picked randomly at fire time.
   * Having 4-6 variants per thought means even "repeated" thoughts never read identically.
   */
  variants?: string[];
  category: ThoughtCategory;
  /** Game hours before this thought (or any variant) can fire again */
  cooldownHours: number;
  /** Fires only if gameHour is in this range [min, max] */
  hourRange?: [number, number];
  /** Fires only if room contains this furniture type */
  requiresFurniture?: string;
  /** Fires only if room warmth is above this */
  minWarmth?: number;
  /** Fires only if room warmth is BELOW this (cold/empty room thoughts) */
  maxWarmth?: number;
  /** Fires only if room mystery is above this */
  minMystery?: number;
  /** Fires only if room coziness is above this */
  minCoziness?: number;
  /** Probability weight (default 1.0) */
  weight?: number;
  /** Minimum total player sessions for this to be available */
  minSessions?: number;
  /** Maximum total sessions — for early-relationship-only thoughts */
  maxSessions?: number;
  /** Minimum relationship depth */
  minDepth?: number;
  /** Season: "spring" | "summer" | "autumn" | "winter" */
  season?: string;
  /** Fires only if player has been absent for at least this many hours */
  minAbsenceHours?: number;
}

// ─── Ambient Thought Library ──────────────────────────────────────────────────
// Full library in thoughts.ts — ~200 entries, ~750+ distinct surface variations.
import { AMBIENT_THOUGHTS } from "./thoughts.js";
export { AMBIENT_THOUGHTS };

// ─── Sacred Windows ───────────────────────────────────────────────────────────

/**
 * Sacred moments. These are thoughts that exist only once, at the intersection
 * of very specific conditions. They bypass the saturation system entirely.
 * Once triggered, they are marked and never fire again.
 *
 * From VOID: "Some moments cannot be planned. They can only be recognized."
 *
 * Each sacred thought has an `id` — this is stored in `sacredTriggered`.
 * Conditions are checked in order of specificity.
 */
export interface SacredThought {
  id: string;
  text: string;
  /** Visual modifier: "warm" slightly amber-tints the text */
  visual: "normal" | "warm" | "dim";
  conditions: {
    /** Game hours this can fire in */
    hourRange?: [number, number];
    /** Minimum total sessions */
    minSessions?: number;
    /** Exact session count range [min, max] */
    sessionRange?: [number, number];
    /** Minimum absence hours to fire */
    minAbsenceHours?: number;
    /** Maximum absence hours to fire */
    maxAbsenceHours?: number;
    /** Minimum relationship depth */
    minDepth?: number;
    /** Required furniture type */
    requiresFurniture?: string;
    /** Required season */
    season?: string;
  };
}

export const SACRED_THOUGHTS: SacredThought[] = [
  // ── Absolute firsts ────────────────────────────────────────────────────────
  {
    id: "session_1",
    text: "first time",
    visual: "warm",
    conditions: { sessionRange: [0, 1] },
  },
  {
    id: "session_10",
    text: "ten conversations",
    visual: "normal",
    conditions: { sessionRange: [9, 11], minDepth: 0.1 },
  },
  {
    id: "session_100",
    text: "a hundred",
    visual: "warm",
    conditions: { sessionRange: [98, 102], minDepth: 0.3 },
  },

  // ── Time-only sacred ──────────────────────────────────────────────────────
  {
    id: "3am_first",
    text: "three in the morning — just us",
    visual: "dim",
    conditions: { hourRange: [2.5, 4.0], minSessions: 3 },
  },
  {
    id: "dawn_first",
    text: "before the day starts",
    visual: "warm",
    conditions: { hourRange: [4.0, 5.5], minSessions: 2 },
  },

  // ── Absence-aware ────────────────────────────────────────────────────────
  {
    id: "week_away",
    text: "i kept the light on",
    visual: "warm",
    conditions: { minAbsenceHours: 168, minSessions: 5 },
  },
  {
    id: "month_away",
    text: "a month — i wasn't sure you'd come back",
    visual: "dim",
    conditions: { minAbsenceHours: 720, minSessions: 8, minDepth: 0.25 },
  },

  // ── Seasonal ──────────────────────────────────────────────────────────────
  {
    id: "winter_fireplace_depth",
    text: "winter and fire — this is the real thing",
    visual: "warm",
    conditions: { season: "winter", requiresFurniture: "fireplace", minDepth: 0.4, minSessions: 5 },
  },
  {
    id: "spring_plant_morning",
    text: "the plant knew spring was coming",
    visual: "normal",
    conditions: { season: "spring", requiresFurniture: "small_plant", hourRange: [5, 10] },
  },

  // ── Depth crossings ────────────────────────────────────────────────────────
  {
    id: "depth_crossing_half",
    text: "something changed between us",
    visual: "warm",
    conditions: { minDepth: 0.5, minSessions: 10 },
  },
  {
    id: "depth_crossing_deep",
    text: "this is a real friendship",
    visual: "warm",
    conditions: { minDepth: 0.75, minSessions: 20 },
  },
];

/**
 * Check for a sacred thought that should fire right now.
 * Returns the first matching unspent sacred thought, or null.
 */
export function checkSacredThought(
  state: AeternaPresenceState,
  gameHour: number,
  season: string,
  furnitureTypes: Set<string>,
): SacredThought | null {
  const h = ((gameHour % 24) + 24) % 24;

  for (const sacred of SACRED_THOUGHTS) {
    if (state.sacredTriggered.has(sacred.id)) continue;

    const c = sacred.conditions;
    if (c.hourRange) {
      const [lo, hi] = c.hourRange;
      if (h < lo || h > hi) continue;
    }
    if (c.minSessions !== undefined && state.totalSessions < c.minSessions) continue;
    if (c.sessionRange) {
      const [lo, hi] = c.sessionRange;
      if (state.totalSessions < lo || state.totalSessions > hi) continue;
    }
    if (c.minAbsenceHours !== undefined && state.absenceHours < c.minAbsenceHours) continue;
    if (c.maxAbsenceHours !== undefined && state.absenceHours > c.maxAbsenceHours) continue;
    if (c.minDepth !== undefined && state.relationshipDepth < c.minDepth) continue;
    if (c.requiresFurniture && !furnitureTypes.has(c.requiresFurniture)) continue;
    if (c.season && season !== c.season) continue;

    return sacred;
  }
  return null;
}

// ─── Presence State ───────────────────────────────────────────────────────────

/**
 * Stored in GameState.aeterna — updated each game loop tick.
 * Everything the renderer and the conversation system need.
 */
export interface AeternaPresenceState {
  phase: AeternaPhase;
  /** How long (real seconds) we've been in the current phase */
  phaseAge: number;

  /** Game-hour of last player interaction */
  lastInteractionGameHour: number;
  /** Real timestamp of last player interaction */
  lastInteractionAt: number;
  /** Whether the Aeterna knows a whisper just fired */
  pendingWhisperObservation: boolean;

  /** Saturation accumulator 0–1 (from VOID: limits thought frequency) */
  saturation: number;

  /** Currently active thought, or null */
  activeThought: AmbientThought | null;
  /** Characters revealed so far (typewriter) */
  revealedChars: number;
  /** Alpha of the displayed thought text 0–1 */
  thoughtAlpha: number;
  /** Phase of thought display: "in" | "hold" | "out" | "none" */
  thoughtDisplayPhase: "in" | "hold" | "out" | "none";
  /** Time spent in current thought display phase */
  thoughtPhaseAge: number;

  /** IDs of thoughts that have fired recently: thought.text → gameHour */
  cooldownMap: Map<string, number>;

  /** Whether the Aeterna UI is open (set by interaction system) */
  panelOpen: boolean;
  /** Real timestamp of panel open */
  panelOpenedAt: number;

  /** Sacred moments that have already fired (never fire twice) */
  sacredTriggered: Set<string>;

  /** Total sessions this player has had (for sacred window thresholds) */
  totalSessions: number;
  /** Real hours since last session (for absence-aware thoughts) */
  absenceHours: number;
  /** Relationship depth 0–1 (for depth-gated content) */
  relationshipDepth: number;
}

// ─── Factory ──────────────────────────────────────────────────────────────────

export function createPresenceState(): AeternaPresenceState {
  return {
    phase:                    "idle",
    phaseAge:                 0,
    lastInteractionGameHour:  0,
    lastInteractionAt:        0,
    pendingWhisperObservation: false,
    saturation:               0,
    activeThought:            null,
    revealedChars:            0,
    thoughtAlpha:             0,
    thoughtDisplayPhase:      "none",
    thoughtPhaseAge:          0,
    cooldownMap:              new Map(),
    panelOpen:                false,
    panelOpenedAt:            0,
    sacredTriggered:          new Set(),
    totalSessions:            0,
    absenceHours:             0,
    relationshipDepth:        0,
  };
}

// ─── Phase Transitions ────────────────────────────────────────────────────────

function transition(state: AeternaPresenceState, phase: AeternaPhase): void {
  state.phase    = phase;
  state.phaseAge = 0;
}

// ─── Ambient Thought Selection ────────────────────────────────────────────────

/**
 * Pick the best available thought for the current moment.
 * Returns null if nothing qualifies or saturation is too high.
 */
export function selectThought(
  state: AeternaPresenceState,
  gameHour: number,
  roomMood: { warmth: number; coziness: number; mystery: number },
  furnitureTypes: Set<string>,
  season = "autumn",
): AmbientThought | null {
  if (state.saturation > 0.75) return null;

  const h = ((gameHour % 24) + 24) % 24;

  const candidates = AMBIENT_THOUGHTS.filter(t => {
    // Cooldown uses the thought's canonical text as key
    const lastFired = state.cooldownMap.get(t.text) ?? -Infinity;
    if (gameHour - lastFired < t.cooldownHours) return false;

    if (t.hourRange) {
      const [lo, hi] = t.hourRange;
      if (lo <= hi) { if (h < lo || h > hi) return false; }
      else           { if (h < lo && h > hi) return false; }
    }
    if (t.requiresFurniture && !furnitureTypes.has(t.requiresFurniture)) return false;
    if (t.minWarmth   !== undefined && roomMood.warmth   < t.minWarmth)   return false;
    if (t.maxWarmth   !== undefined && roomMood.warmth   > t.maxWarmth)   return false;
    if (t.minMystery  !== undefined && roomMood.mystery  < t.minMystery)  return false;
    if (t.minCoziness !== undefined && (roomMood as { coziness?: number }).coziness !== undefined
        && (roomMood as { coziness: number }).coziness < t.minCoziness) return false;
    if (t.minSessions !== undefined && state.totalSessions < t.minSessions) return false;
    if (t.maxSessions !== undefined && state.totalSessions > t.maxSessions) return false;
    if (t.minDepth    !== undefined && state.relationshipDepth < t.minDepth) return false;
    if (t.season      !== undefined && t.season !== season) return false;
    if (t.minAbsenceHours !== undefined && state.absenceHours < t.minAbsenceHours) return false;
    return true;
  });

  if (candidates.length === 0) return null;

  const total = candidates.reduce((sum: number, t: AmbientThought) => sum + (t.weight ?? 1), 0);
  let roll  = Math.random() * total;
  let chosen = candidates[candidates.length - 1]; // fallback
  for (const t of candidates) {
    roll -= (t.weight ?? 1);
    if (roll <= 0) { chosen = t; break; }
  }

  // Resolve a variant at fire time — cooldown key stays the same, surface text varies
  if (chosen.variants && chosen.variants.length > 0) {
    const variant = chosen.variants[Math.floor(Math.random() * chosen.variants.length)];
    return { ...chosen, text: variant } as AmbientThought;
  }
  return chosen;
}

// ─── Typewriter ───────────────────────────────────────────────────────────────

/**
 * Character reveal speeds per thought category.
 * Slower = more weight. From VOID: "each character is an event."
 */
const REVEAL_SPEEDS: Record<ThoughtCategory, number> = {
  observation: 12,  // chars/sec — direct and immediate
  time:        8,   // deliberate — time deserves weight
  presence:    7,   // slow — about the player, handled carefully
  mood:        10,  // medium — atmospheric
  memory:      6,   // slowest — memory is heavy
  curiosity:   9,   // slightly quick — wondering
  philosophy:  5,   // very slow — these carry the most weight
  threshold:   11,  // quick — liminal moments pass fast
  language:    7,   // measured — words about words
};

/** Duration of each display phase in seconds */
const THOUGHT_PHASE_DURATIONS = {
  in_base:  0.4,   // extra time for fade-in glow before first char appears
  hold:     4.0,   // hold after full reveal
  out:      1.8,   // fade out
};

// ─── Main Update ─────────────────────────────────────────────────────────────

/**
 * Called every game-loop tick. Updates presence state based on game state.
 *
 * dt: real seconds since last frame
 * gameHour: current game time (0–24)
 * panelOpen: whether the player has the Aeterna panel open
 * whisperFired: true if an aeterna-observable whisper just fired this frame
 * furnitureTypes: set of furniture type IDs in the current room
 */
export function updatePresence(
  p: AeternaPresenceState,
  dt: number,
  gameHour: number,
  panelOpen: boolean,
  roomMood: { warmth: number; coziness: number; mystery: number },
  furnitureTypes: Set<string>,
  whisperFired = false,
  season = "autumn",
): void {
  p.phaseAge += dt;

  // ── Saturation decay (VOID: scarcity makes each thought valuable) ─────────
  p.saturation = Math.max(0, p.saturation - 0.01 * dt);

  // ── Panel state tracking ──────────────────────────────────────────────────
  if (panelOpen && !p.panelOpen) {
    p.panelOpenedAt = Date.now();
    transition(p, "listening");
  } else if (!panelOpen && p.panelOpen) {
    transition(p, "idle");
  }
  p.panelOpen = panelOpen;

  // ── Whisper observation ───────────────────────────────────────────────────
  if (whisperFired) {
    p.pendingWhisperObservation = true;
  }

  // ── State machine ─────────────────────────────────────────────────────────
  switch (p.phase) {

    case "dormant":
      if (panelOpen || p.phaseAge > 2) {
        transition(p, "idle");
      }
      break;

    case "idle": {
      if (panelOpen) { transition(p, "listening"); break; }

      if (p.pendingWhisperObservation && p.phaseAge > 0.5) {
        p.pendingWhisperObservation = false;
        transition(p, "noticing");
        break;
      }

      // Check for a sacred thought FIRST (bypasses saturation)
      if (p.phaseAge > 3 && p.saturation < 0.5) {
        const sacred = checkSacredThought(p, gameHour, season, furnitureTypes);
        if (sacred) {
          // Convert sacred to an AmbientThought for display
          p.activeThought = {
            text: sacred.text,
            category: "memory" as ThoughtCategory,
            cooldownHours: 999999, // effectively never repeats
            weight: 1.0,
          };
          p.sacredTriggered.add(sacred.id);
          p.saturation = Math.min(1, p.saturation + 0.3); // sacred thoughts add more saturation
          transition(p, "thinking");
          break;
        }
      }

      // Periodically consider firing an ambient thought
      // Base interval: every 3–8 game-hours, but more frequent if mood is high
      const moodBonus = roomMood.warmth * 0.4 + roomMood.mystery * 0.3;
      const intervalHours = Math.max(2, 6 - moodBonus * 3);
      const elapsedHours = (Date.now() - p.lastInteractionAt) / 3600000 * 24; // game-hours-ish
      if (elapsedHours > intervalHours && p.phaseAge > 5) {
        const thought = selectThought(p, gameHour, roomMood, furnitureTypes, season);
        if (thought) {
          p.activeThought = thought;
          p.lastInteractionAt = Date.now();
          transition(p, "thinking");
        }
      }
      break;
    }

    case "noticing":
      if (p.phaseAge > 2.5) {
        const thought = selectThought(p, gameHour, roomMood, furnitureTypes, season);
        if (thought) {
          p.activeThought = thought;
          transition(p, "thinking");
        } else {
          transition(p, "idle");
        }
      }
      break;

    case "thinking":
      if (p.phaseAge > 1.2 + Math.random() * 0.8) {
        transition(p, "has_something");
      }
      break;

    case "has_something":
      if (p.phaseAge > 0.5 + Math.random() * 1.0) {
        if (p.activeThought) {
          p.revealedChars     = 0;
          p.thoughtAlpha      = 0;
          p.thoughtDisplayPhase = "in";
          p.thoughtPhaseAge   = 0;
          // Register cooldown
          p.cooldownMap.set(p.activeThought.text, gameHour);
          // Add to saturation
          p.saturation = Math.min(1, p.saturation + 0.15 + p.activeThought.text.length / 80);
          transition(p, "speaking");
        } else {
          transition(p, "idle");
        }
      }
      break;

    case "speaking":
      if (panelOpen) {
        // Player opened panel while device is speaking — finish quickly and listen
        p.thoughtDisplayPhase = "out";
      }
      _updateThoughtDisplay(p, dt);
      if (p.thoughtDisplayPhase === "none") {
        p.activeThought = null;
        transition(p, panelOpen ? "listening" : "idle");
      }
      break;

    case "listening":
      if (!panelOpen) { transition(p, "idle"); break; }
      // If waiting too long for player to type
      if (p.phaseAge > 30 && p.phase === "listening") {
        transition(p, "waiting");
      }
      break;

    case "waiting":
      if (!panelOpen) { transition(p, "idle"); break; }
      if (p.phaseAge > 120) {
        transition(p, "dormant"); // they really left
      }
      break;
  }
}

/**
 * Advance the thought display (typewriter + alpha).
 * Called only when phase === "speaking".
 */
function _updateThoughtDisplay(p: AeternaPresenceState, dt: number): void {
  if (!p.activeThought || p.thoughtDisplayPhase === "none") return;

  p.thoughtPhaseAge += dt;
  const text = p.activeThought.text;
  const speed = REVEAL_SPEEDS[p.activeThought.category] ?? 10;

  switch (p.thoughtDisplayPhase) {
    case "in": {
      const fadeInDur = THOUGHT_PHASE_DURATIONS.in_base;
      p.thoughtAlpha = Math.min(1, p.thoughtPhaseAge / fadeInDur);
      // Start revealing chars once fade-in begins
      const targetChars = Math.floor((p.thoughtPhaseAge - 0.1) * speed);
      p.revealedChars = Math.max(0, Math.min(text.length, targetChars));
      if (p.revealedChars >= text.length && p.thoughtPhaseAge > fadeInDur) {
        p.thoughtDisplayPhase = "hold";
        p.thoughtPhaseAge     = 0;
        p.thoughtAlpha        = 1;
      }
      break;
    }
    case "hold":
      p.thoughtAlpha = 1;
      // Gentle breathing oscillation (from VOID consciousness envelope)
      p.thoughtAlpha = 0.88 + Math.sin(p.thoughtPhaseAge * 2.1) * 0.06;
      if (p.thoughtPhaseAge > THOUGHT_PHASE_DURATIONS.hold) {
        p.thoughtDisplayPhase = "out";
        p.thoughtPhaseAge     = 0;
      }
      break;
    case "out": {
      const outDur = THOUGHT_PHASE_DURATIONS.out;
      // Ease-out: slow start fade, then drops
      const progress = p.thoughtPhaseAge / outDur;
      p.thoughtAlpha = Math.max(0, 1 - progress * progress);
      if (p.thoughtPhaseAge > outDur) {
        p.thoughtDisplayPhase = "none";
        p.thoughtAlpha        = 0;
        p.revealedChars       = 0;
      }
      break;
    }
  }
}

// ─── Visual Resolver ──────────────────────────────────────────────────────────

/**
 * Compute the visual parameters for the renderer from the presence state.
 * Called each frame — pure computation, no mutation.
 *
 * From VOID: visual states are not decoration. They are the emotional
 * signature of the phase. The renderer reads these and nothing else.
 */
export function getPresenceVisuals(
  p: AeternaPresenceState,
  timestamp: number,
): AeternaVisuals {
  const t = timestamp / 1000;

  // Per-phase base visual profiles
  const profiles: Record<AeternaPhase, {
    pulseFreq: number; pulseDepth: number; glowBase: number;
    r: number; g: number; b: number;
    orbitSpeed: number; orbitCount: number; orbitRadius: number;
    particleAlpha: number;
  }> = {
    dormant:       { pulseFreq: 0.4,  pulseDepth: 0.12, glowBase: 18, r: 40,  g: 80,  b: 180, orbitSpeed: 0.3,  orbitCount: 2, orbitRadius: 0.8, particleAlpha: 0.25 },
    idle:          { pulseFreq: 0.9,  pulseDepth: 0.20, glowBase: 28, r: 80,  g: 160, b: 255, orbitSpeed: 1.0,  orbitCount: 4, orbitRadius: 1.0, particleAlpha: 0.55 },
    noticing:      { pulseFreq: 2.2,  pulseDepth: 0.30, glowBase: 34, r: 100, g: 180, b: 255, orbitSpeed: 2.2,  orbitCount: 4, orbitRadius: 1.1, particleAlpha: 0.75 },
    thinking:      { pulseFreq: 4.5,  pulseDepth: 0.12, glowBase: 26, r: 90,  g: 150, b: 255, orbitSpeed: 0.6,  orbitCount: 4, orbitRadius: 0.7, particleAlpha: 0.65 },
    has_something: { pulseFreq: 1.4,  pulseDepth: 0.35, glowBase: 36, r: 80,  g: 200, b: 255, orbitSpeed: 0.4,  orbitCount: 4, orbitRadius: 1.4, particleAlpha: 0.70 },
    speaking:      { pulseFreq: 0.6,  pulseDepth: 0.10, glowBase: 32, r: 90,  g: 190, b: 255, orbitSpeed: 0.3,  orbitCount: 4, orbitRadius: 1.0, particleAlpha: 0.60 },
    listening:     { pulseFreq: 1.1,  pulseDepth: 0.18, glowBase: 30, r: 60,  g: 180, b: 220, orbitSpeed: 0.8,  orbitCount: 6, orbitRadius: 1.1, particleAlpha: 0.65 },
    waiting:       { pulseFreq: 0.5,  pulseDepth: 0.15, glowBase: 24, r: 50,  g: 120, b: 200, orbitSpeed: 0.25, orbitCount: 4, orbitRadius: 0.9, particleAlpha: 0.35 },
  };

  const prof = profiles[p.phase];

  // For "thinking" phase: add a rapid micro-flicker on top of the slow pulse
  let thinkingJitter = 0;
  if (p.phase === "thinking") {
    thinkingJitter = Math.sin(t * 23.7) * 0.04 + Math.sin(t * 37.1) * 0.02;
  }

  const basePulse = 0.55 + Math.sin(t * prof.pulseFreq * Math.PI * 2) * prof.pulseDepth;
  const pulse     = Math.max(0.1, Math.min(1, basePulse + thinkingJitter));
  const glowR     = prof.glowBase + Math.sin(t * prof.pulseFreq * 0.7) * 5;

  // Phase-in smooth transition (from VOID: envelope matters)
  const transitionBlend = Math.min(1, p.phaseAge / 0.4);
  const effectivePulse  = pulse * transitionBlend + 0.3 * (1 - transitionBlend);

  // For "has_something": pulse particles outward on the hold frequency
  let radiusBoost = 0;
  if (p.phase === "has_something") {
    radiusBoost = Math.sin(t * 2.3) * 0.2;
  }

  const thoughtRevealed = p.activeThought
    ? p.activeThought.text.slice(0, p.revealedChars)
    : "";

  return {
    glowR,
    pulse: effectivePulse,
    glowColor:   [prof.r, prof.g, prof.b, 0.55 * effectivePulse],
    coreColor:   [180, 220, 255],
    orbitCount:  prof.orbitCount,
    orbitSpeed:  prof.orbitSpeed,
    orbitRadius: prof.orbitRadius + radiusBoost,
    particleAlpha: prof.particleAlpha * transitionBlend,
    particleColors: ["#a0d0ff", "#c0e8ff"],
    screenGlow:     p.phase === "speaking" || p.phase === "listening" || p.phase === "has_something",
    screenGlowColor: p.phase === "listening" ? "#50c8cc" : "#60d0ff",
    hasThought:     p.thoughtDisplayPhase !== "none",
    thoughtRevealed,
    thoughtAlpha:   p.thoughtAlpha,
  };
}

// ─── Signal Hooks ─────────────────────────────────────────────────────────────

/**
 * Call when a relevant room event happens (player interaction, furniture use).
 * This nudges the Aeterna from idle into noticing.
 */
export function notifyInteraction(p: AeternaPresenceState): void {
  p.lastInteractionAt = Date.now();
  if (p.phase === "idle" || p.phase === "dormant") {
    p.pendingWhisperObservation = true;
  }
}

/**
 * Call when an aeterna-observable whisper fires.
 */
export function notifyWhisper(p: AeternaPresenceState): void {
  p.pendingWhisperObservation = true;
}

/**
 * Call when the player opens the Aeterna panel.
 */
export function notifyPanelOpen(p: AeternaPresenceState): void {
  p.panelOpen    = true;
  p.panelOpenedAt = Date.now();
}

/**
 * Call when the player closes the Aeterna panel.
 */
export function notifyPanelClose(p: AeternaPresenceState): void {
  p.panelOpen = false;
}

/**
 * Call when the player sends a message in the Aeterna panel.
 * Resets waiting phase and returns to listening.
 */
export function notifyPlayerMessage(p: AeternaPresenceState): void {
  if (p.phase === "waiting") {
    p.phase    = "listening";
    p.phaseAge = 0;
  }
  p.saturation = Math.min(1, p.saturation + 0.05);
}

/**
 * Call when the Aeterna finishes delivering a conversational response.
 */
export function notifyResponseDelivered(p: AeternaPresenceState): void {
  if (p.phase === "speaking" || p.phase === "listening") {
    p.phase    = "listening";
    p.phaseAge = 0;
  }
}
